/**
 * The React Flow canvas. Reads scenario + selection from the store,
 * renders nodes + edges, writes back position changes, selection, and
 * (as of Phase 1b.4) drag-to-connect edges.
 *
 * The drag-to-connect contract: each choice has its own source handle on
 * the bottom of its source node, with `handle.id = choice.id`. Every node
 * has a single top-center target handle. When the user drags a line from
 * source to target, React Flow fires `onConnect` with `{ source, sourceHandle,
 * target, targetHandle }` — we forward to the store's `connectChoice`, which
 * rewrites the choice's fallback destination.
 *
 * Pan/zoom/controls all come free from React Flow.
 */
import { useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  type Connection,
  type NodeChange,
  type NodeTypes,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useScenarioStore } from '../store/scenarioStore';
import { ScenarioNodeCard } from './ScenarioNodeCard';
import {
  scenarioToFlowNodes,
  scenarioToFlowEdges,
  type ScenarioFlowNode,
} from './adapters';

// Register the custom node type. React Flow looks up node.type → component.
// Memoized at module scope so React Flow doesn't see a new object every render
// (which would trigger a warning and unnecessary work).
const nodeTypes: NodeTypes = {
  scenario: ScenarioNodeCard,
};

export function Canvas() {
  const scenario = useScenarioStore((s) => s.scenario);
  const selectedId = useScenarioStore((s) => s.selectedId);
  const selectNode = useScenarioStore((s) => s.selectNode);
  const moveNode = useScenarioStore((s) => s.moveNode);
  const connectChoice = useScenarioStore((s) => s.connectChoice);
  const deleteNode = useScenarioStore((s) => s.deleteNode);
  const getValidation = useScenarioStore((s) => s.getValidation);

  // Recompute validation on every render; cost is sub-millisecond at
  // prototype scale. If this becomes hot, memoize in the store.
  const validation = useMemo(() => getValidation(), [scenario, getValidation]);

  /**
   * Delete-key handling. Backspace or Delete deletes the selected node —
   * but ONLY when focus is NOT inside an input/textarea/contenteditable.
   * Without that guard, typing Backspace in the title field would delete
   * the node, which would be infuriating.
   *
   * The store's `deleteNode` already refuses to delete the start node, so
   * we don't need to check that here.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      // Don't delete when focus is on something editable.
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target.isContentEditable) return;
      // Also leave Backspace alone if the user is typing somewhere we
      // didn't anticipate — anywhere inside an aside is treated as
      // potential input territory.
      if (target.closest('aside, [role="dialog"]')) return;

      if (selectedId) {
        e.preventDefault();
        deleteNode(selectedId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, deleteNode]);

  // Derive React Flow nodes/edges from the scenario + validation. These are
  // recomputed whenever scenario changes — that's the right invariant: the
  // store is the source of truth, the canvas is a view of it.
  const flowNodes = useMemo<ScenarioFlowNode[]>(
    () => scenarioToFlowNodes(scenario, validation).map((n) => ({
      ...n,
      selected: n.id === selectedId,
    })),
    [scenario, validation, selectedId],
  );
  const flowEdges = useMemo(() => scenarioToFlowEdges(scenario), [scenario]);

  /**
   * Handle React Flow's node change events. The ones we care about right now:
   *   - 'position' with `dragging: false` → drag ended; persist the new x/y.
   *   - 'select' → update selection in the store.
   * We deliberately ignore position changes while `dragging: true` to avoid
   * thrashing the store; React Flow keeps its own internal copy during drag.
   */
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Apply visually first so dragging feels responsive
      applyNodeChanges(changes, flowNodes);
      for (const ch of changes) {
        if (ch.type === 'position' && ch.dragging === false && ch.position) {
          moveNode(ch.id, ch.position.x, ch.position.y);
        } else if (ch.type === 'select') {
          if (ch.selected) selectNode(ch.id);
          // We don't clear on deselect-via-React-Flow; clicking blank canvas
          // is the explicit "clear selection" gesture (see onPaneClick below).
        }
      }
    },
    [flowNodes, moveNode, selectNode],
  );

  // Drag-to-connect handler. The sourceHandle on the source side is the choice
  // id (see ScenarioNodeCard); the target node's handle is the single top-center
  // target, so targetHandle isn't meaningful here.
  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || !conn.sourceHandle) return;
      if (conn.source === conn.target) return; // refuse self-loops at the canvas level
      connectChoice(conn.source, conn.sourceHandle, conn.target);
    },
    [connectChoice],
  );

  return (
    <div className="w-full h-full bg-cream">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onConnect={onConnect}
        onPaneClick={() => selectNode(null)}
        // Phase 1b.4: drag-to-connect is on.
        nodesConnectable
        // Edges aren't deletable yet either; deletion lives in the detail panel.
        edgesFocusable={false}
        // Visual tuning
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1}
          color="#D9D2BE"
        />
        <Controls
          showInteractive={false}
          className="!bg-cream-2 !border-cream-border"
        />
      </ReactFlow>
    </div>
  );
}
